/**
 * Block registry.
 *
 * Every block is a single byte id stored in a chunk's Uint8Array, so this list
 * defines the whole world content. `TILE_*` constants index into the procedurally
 * generated texture atlas (see `atlas.ts`).
 */

import { COAL, DIAMOND, STRING } from "./item-ids";

export const TILE = {
  grassTop: 0,
  grassSide: 1,
  dirt: 2,
  stone: 3,
  sand: 4,
  logSide: 5,
  logTop: 6,
  leaves: 7,
  planks: 8,
  water: 9,
  snow: 10,
  gravel: 11,
  cobblestone: 12,
  glass: 13,
  bedrock: 14,
  coalOre: 15,
  ironOre: 16,
  goldOre: 17,
  diamondOre: 18,
  sandstone: 19,
  brick: 20,
  craftingTableTop: 21,
  craftingTableSide: 22,
  furnaceTop: 23,
  furnaceSide: 24,
  chestTop: 25,
  chestSide: 26,
  wool: 27,
  goldBlock: 28,
  torch: 29,
  tntTop: 30,
  tntBottom: 31,
  tntSide: 32,
  cobweb: 33,
} as const;

export const AIR = 0;
export const GRASS = 1;
export const DIRT = 2;
export const STONE = 3;
export const SAND = 4;
export const LOG = 5;
export const LEAVES = 6;
export const PLANKS = 7;
export const WATER = 8;
export const SNOW = 9;
export const GRAVEL = 10;
export const COBBLESTONE = 11;
export const GLASS = 12;
export const BEDROCK = 13;
export const COAL_ORE = 14;
export const IRON_ORE = 15;
export const GOLD_ORE = 16;
export const DIAMOND_ORE = 17;
export const SANDSTONE = 18;
export const BRICKS = 19;
export const CRAFTING_TABLE = 20;
export const FURNACE = 21;
export const CHEST = 22;
export const WHITE_WOOL = 23;
export const GOLD_BLOCK = 24;
export const TORCH = 25;
export const TNT = 26;
export const COBWEB = 27;

export const BLOCK_COUNT = 28;

/** Tool classes that speed up mining or provide weapon actions. */
export type ToolKind = "pickaxe" | "axe" | "shovel" | "sword" | "bow";

export interface BlockDef {
  readonly id: number;
  readonly name: string;
  /** Texture tile per face. */
  readonly top: number;
  readonly side: number;
  readonly bottom: number;
  /** Has a full cube collision volume. */
  readonly solid: boolean;
  /** Hides the neighbouring faces of adjacent blocks (face culling). */
  readonly opaque: boolean;
  /** Rendered in the transparent pass with a lowered surface. */
  readonly liquid: boolean;
  /** Can be selected in the hotbar / creative inventory. */
  readonly placeable: boolean;
  /** Seconds of held mining needed to break the block. Infinity = unbreakable. */
  readonly hardness: number;
  /** Block id granted when mined in survival, or -1 for no drop. Non-block
   * item ids are allowed (coal ore drops coal). */
  readonly drop: number;
  /** Tool class that mines this block faster. */
  readonly tool?: ToolKind;
  /** Minimum tool tier needed to receive the drop. 0 = hand-harvestable. */
  readonly minTier: number;
}

function def(
  id: number,
  name: string,
  top: number,
  side: number,
  bottom: number,
  opts: Partial<Omit<BlockDef, "id" | "name" | "top" | "side" | "bottom">> = {},
): BlockDef {
  return {
    id,
    name,
    top,
    side,
    bottom,
    solid: opts.solid ?? true,
    opaque: opts.opaque ?? true,
    liquid: opts.liquid ?? false,
    placeable: opts.placeable ?? true,
    hardness: opts.hardness ?? 1,
    drop: opts.drop ?? id,
    tool: opts.tool,
    minTier: opts.minTier ?? 0,
  };
}

/** Indexed by block id. */
export const BLOCKS: readonly BlockDef[] = [
  def(AIR, "Air", -1, -1, -1, { solid: false, opaque: false, placeable: false, hardness: 0, drop: -1 }),
  def(GRASS, "Grass Block", TILE.grassTop, TILE.grassSide, TILE.dirt, {
    hardness: 0.6,
    drop: DIRT,
    tool: "shovel",
  }),
  def(DIRT, "Dirt", TILE.dirt, TILE.dirt, TILE.dirt, { hardness: 0.5, tool: "shovel" }),
  def(STONE, "Stone", TILE.stone, TILE.stone, TILE.stone, {
    hardness: 1.5,
    drop: COBBLESTONE,
    tool: "pickaxe",
    minTier: 1,
  }),
  def(SAND, "Sand", TILE.sand, TILE.sand, TILE.sand, { hardness: 0.5, tool: "shovel" }),
  def(LOG, "Oak Log", TILE.logTop, TILE.logSide, TILE.logTop, { hardness: 1.2, tool: "axe" }),
  def(LEAVES, "Oak Leaves", TILE.leaves, TILE.leaves, TILE.leaves, { hardness: 0.2 }),
  def(PLANKS, "Oak Planks", TILE.planks, TILE.planks, TILE.planks, {
    hardness: 1.0,
    tool: "axe",
  }),
  def(WATER, "Water", TILE.water, TILE.water, TILE.water, {
    solid: false,
    opaque: false,
    liquid: true,
    hardness: Infinity,
    drop: -1,
  }),
  def(SNOW, "Snow Block", TILE.snow, TILE.snow, TILE.dirt, {
    hardness: 0.35,
    tool: "shovel",
  }),
  def(GRAVEL, "Gravel", TILE.gravel, TILE.gravel, TILE.gravel, {
    hardness: 0.6,
    tool: "shovel",
  }),
  def(COBBLESTONE, "Cobblestone", TILE.cobblestone, TILE.cobblestone, TILE.cobblestone, {
    hardness: 1.5,
    tool: "pickaxe",
    minTier: 1,
  }),
  def(GLASS, "Glass", TILE.glass, TILE.glass, TILE.glass, { opaque: false, hardness: 0.3 }),
  def(BEDROCK, "Bedrock", TILE.bedrock, TILE.bedrock, TILE.bedrock, {
    placeable: false,
    hardness: Infinity,
    drop: -1,
  }),
  def(COAL_ORE, "Coal Ore", TILE.coalOre, TILE.coalOre, TILE.coalOre, {
    hardness: 2.0,
    tool: "pickaxe",
    minTier: 1,
    drop: COAL,
  }),
  def(IRON_ORE, "Iron Ore", TILE.ironOre, TILE.ironOre, TILE.ironOre, {
    hardness: 2.5,
    tool: "pickaxe",
    minTier: 2,
  }),
  def(GOLD_ORE, "Gold Ore", TILE.goldOre, TILE.goldOre, TILE.goldOre, {
    hardness: 3.0,
    tool: "pickaxe",
    minTier: 2,
  }),
  def(DIAMOND_ORE, "Diamond Ore", TILE.diamondOre, TILE.diamondOre, TILE.diamondOre, {
    hardness: 3.5,
    tool: "pickaxe",
    minTier: 2,
    drop: DIAMOND,
  }),
  def(SANDSTONE, "Sandstone", TILE.sandstone, TILE.sandstone, TILE.sandstone, {
    hardness: 1.2,
    tool: "pickaxe",
    minTier: 1,
  }),
  def(BRICKS, "Bricks", TILE.brick, TILE.brick, TILE.brick, {
    hardness: 1.5,
    tool: "pickaxe",
    minTier: 1,
  }),
  def(CRAFTING_TABLE, "Crafting Table", TILE.craftingTableTop, TILE.craftingTableSide, TILE.planks, {
    hardness: 2.0,
    tool: "axe",
  }),
  def(FURNACE, "Furnace", TILE.furnaceTop, TILE.furnaceSide, TILE.furnaceTop, {
    hardness: 2.5,
    tool: "pickaxe",
    minTier: 1,
  }),
  def(CHEST, "Chest", TILE.chestTop, TILE.chestSide, TILE.chestTop, {
    hardness: 2.5,
    tool: "axe",
  }),
  def(WHITE_WOOL, "White Wool", TILE.wool, TILE.wool, TILE.wool, { hardness: 0.8 }),
  def(GOLD_BLOCK, "Block of Gold", TILE.goldBlock, TILE.goldBlock, TILE.goldBlock, {
    hardness: 3.0,
    tool: "pickaxe",
    minTier: 2,
  }),
  def(TORCH, "Torch", TILE.torch, TILE.torch, TILE.torch, {
    solid: false,
    opaque: false,
    hardness: 0,
    drop: TORCH,
  }),
  def(TNT, "TNT", TILE.tntTop, TILE.tntSide, TILE.tntBottom, {
    hardness: 0,
    drop: TNT,
  }),
  def(COBWEB, "Cobweb", TILE.cobweb, TILE.cobweb, TILE.cobweb, {
    solid: false,
    opaque: false,
    hardness: 4.0,
    drop: STRING,
    tool: "sword",
  }),
];

export function isSolid(id: number): boolean {
  return id > 0 && BLOCKS[id].solid;
}

export function isOpaque(id: number): boolean {
  return id > 0 && BLOCKS[id].opaque;
}

export function isLiquid(id: number): boolean {
  return id > 0 && BLOCKS[id].liquid;
}

export function blockName(id: number): string {
  return id > 0 && id < BLOCKS.length ? BLOCKS[id].name : "Unknown";
}

/**
 * Explosion blast resistance per block type matching authentic Minecraft 1.8.8 values.
 * Bedrock is immune (18,000,000), Water absorbs blast energy (500), Stone/Cobble/Bricks have 30,
 * Wood/Planks have 15, Dirt/Grass/Sand have ~3, Glass/Leaves/Wool have ~1, and TNT/Torches have 0.
 */
export function blastResistance(id: number): number {
  switch (id) {
    case BEDROCK:
      return 18_000_000;
    case WATER:
      return 500;
    case STONE:
    case COBBLESTONE:
    case BRICKS:
    case FURNACE:
      return 30;
    case IRON_ORE:
    case GOLD_ORE:
    case DIAMOND_ORE:
    case COAL_ORE:
    case GOLD_BLOCK:
      return 15;
    case COBWEB:
      return 20;
    case LOG:
    case PLANKS:
    case CRAFTING_TABLE:
    case CHEST:
      return 15;
    case SANDSTONE:
      return 4;
    case DIRT:
    case GRASS:
    case GRAVEL:
    case SAND:
    case SNOW:
      return 3;
    case LEAVES:
    case GLASS:
    case WHITE_WOOL:
      return 1;
    default:
      return 0;
  }
}

/**
 * Should the face of `self` be emitted, given the block in the neighbouring cell?
 *
 * - Air always exposes the face.
 * - Water only draws against open air. Any other neighbour already draws its own
 *   face on that boundary (solids draw against water, and glass and leaves do
 *   too), so a water quad there would sit exactly on top of it and z-fight —
 *   that is what made underwater terrain flicker white. Culling every non-air
 *   neighbour also keeps the ocean surface a single layer.
 * - Solid blocks draw against anything non-opaque (glass, leaves, water).
 */
export function shouldRenderFace(self: number, neighbour: number): boolean {
  if (neighbour === AIR) return true;
  if (isLiquid(self)) return false;
  return !isOpaque(neighbour);
}

/** Blocks offered in the hotbar, in slot order. */
export const HOTBAR_BLOCKS: readonly number[] = [
  GRASS,
  DIRT,
  STONE,
  COBBLESTONE,
  SAND,
  SANDSTONE,
  LOG,
  PLANKS,
  LEAVES,
  GLASS,
  BRICKS,
  SNOW,
  GRAVEL,
  COAL_ORE,
  IRON_ORE,
  GOLD_ORE,
  DIAMOND_ORE,
  WHITE_WOOL,
  TORCH,
  TNT,
  COBWEB,
];
